import React, { createContext, useContext, useState, useEffect } from 'react';

//  Image imports
import MailIcon from '../assets/images/Email.svg';
import ChannelsIcon from '../assets/images/CRM.svg';
import CalendarIcon from '../assets/images/Calendar.svg';
import TaskIcon from '../assets/images/tasks.png';
import ChennelIcon from '../assets/images/message.png';
import AccountsIcon from '../assets/images/Accounting.svg';
import RetailIcon from '../assets/images/Retail.svg';
import HospitalityIcon from '../assets/images/HMS.png';
import FinanceIcon from '../assets/images/Finance.svg';
var TodoIcons = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAL1SURBVHgB7VVPaxNBFH8zidHWViNoUpHWePGcD6DQiBSqIlUEqxcdBEFE/AhZP4FfYUHEQlEqBf8cdAsF8daeRFCwR2+phPqn7b7xzezOZrLZZJOaXCQPktnd2Znfn/fmLcAw/ve47v18OL+y9W1+9Vc1aZ7BAOPau60qATiMA3CFlOHOwtmRR/Y7HAYUV9/Wq9KXjpQSJALQAOCjE3diIA5cfl2v8kg5AzXSQCNrcaLvDlxarlfBUo40YuiAvqef7URfHbjwsk45R4eRXK5Ua+WsSb0ao5qQWMkmbXTsplfOwO4cYNb9vlDZ6AZ85vkPyrnvhDuTUqbVcSYDNKRnigRK/T4SK8nYJksC5ww9Wpen2w1aVUkjcX6xRjlnTqCU8HhcdehEOOpnEsTizJjLOoCb6Eji3LNalfZ04vZGgIaENU9z4gWBq/UsBbwjiemnBA7SacmxccJSbU4C51wszQbgEYEU8EQSZ57UdMFx1qywyXIWpsPcZ0AsXzzk2puyiXmvJDO4lgLeRKI0W77FNXioKkYiAG4oV/MZQLF85Ygb3zBLZTndJbiK0uHTxTWJmMcweaqRIKjKZkFTwaDq1ZkHLlW5A2ZQvEoA1+t/5/iSBLYOXcT4qSKMTRXyqrmoxoIY4OnrpoYj9XM9z6R40wYcIKyB/G0vv39besS73O7Fg6UijJcmoGF747gp5cZ2XXgQXAND8f7GURc6hHZt061s/smxSjsnRk8WYXSqCKq9NhQrhbLhAhhHZHC9mw4eOWAiyYkDk0UYIQKcASQVXaITKMWqSAePHDARdyJH4LnJQkOxnW/biTD/qu4k+l2DtxCIk9h3ohABtCNhik2lB3xffBAFF3qIxM+xIeFv767bKm0S8Wf0Lz7e6Q1cRcfPcelxLc/4jkcpLkf5Nh3OrgE6aut3ewdPJWCTILAyTyBBHohP9/YG3hUBm0TcCYIXn+/vHbxrAkkkqMeLL/8I3hMBQwJgZy6b9Te+Pji+AsMYRh/iL5mvmIzqBzX8AAAAAElFTkSuQmCC";

// 📦 App item structure
export interface AppItem {
  label: string;
  icon: string; // image path
  path: string;
}

interface SerializableAppItem {
  label: string;
  path: string;
}

interface AppContextType {
  pinnedApps: AppItem[];
  pinApp: (app: AppItem) => void;
  unpinApp: (label: string) => void;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  editPromptClicked: boolean;
  setEditPromptClicked: React.Dispatch<React.SetStateAction<boolean>>;
}

// 🧠 Global context
const AppContext = createContext<AppContextType | undefined>(undefined);

// 🔗 Label-to-icon mapping
const iconMap: Record<string, string> = {
  Mail: MailIcon,
  CRM: ChannelsIcon,
  Calendar: CalendarIcon,
  Task: TaskIcon,
  Channels: ChennelIcon,
  Accounts: AccountsIcon,
  Todo:TodoIcons,
  Finance:FinanceIcon,
  Retail:RetailIcon,
  HMS:HospitalityIcon,

};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pinnedApps, setPinnedApps] = useState<AppItem[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editPromptClicked, setEditPromptClicked] = useState(false);

  useEffect(() => {
    const data = localStorage.getItem('pinnedApps');
    if (data) {
      const parsed: SerializableAppItem[] = JSON.parse(data);
      const restored: AppItem[] = parsed.map(({ label, path }) => ({
        label,
        path,
        icon: iconMap[label] || '',
      }));
      setPinnedApps(restored);
    }
  }, []);

  const persistToStorage = (apps: AppItem[]) => {
    const toStore: SerializableAppItem[] = apps.map(({ label, path }) => ({ label, path }));
    localStorage.setItem('pinnedApps', JSON.stringify(toStore));
  };

  const pinApp = (app: AppItem) => {
    setPinnedApps((prev) => {
      const exists = prev.some((a) => a.label === app.label);
      const newList = exists ? prev : [...prev, app];
      persistToStorage(newList);
      return newList;
    });

    //  DO NOT reset isEditing or editPromptClicked here
    // Let "Done" button control edit state
  };

  const unpinApp = (label: string) => {
    setPinnedApps((prev) => {
      const newList = prev.filter((a) => a.label !== label);
      persistToStorage(newList);
      return newList;
    });
  };

  return (
    <AppContext.Provider
      value={{
        pinnedApps,
        pinApp,
        unpinApp,
        isEditing,
        setIsEditing,
        editPromptClicked,
        setEditPromptClicked,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used within AppProvider");
  return context;
};
